import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CATEGORIES, getCategoryColor, FEEDBACK_TYPES, getFeedbackTypeColor, FEEDBACK_SOURCES, getSourceColor } from '../utils/constants';
import { exportFeedbackToExcel } from '../utils/excelExport';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiSearch, FiMessageSquare, FiThumbsUp, FiPlus, FiTrendingUp, FiClock, FiLoader, FiDownload, FiCheckCircle, FiAlertTriangle } = FiIcons;

const FeedbackPage = ({ onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // 'recent' or 'upvotes'
  const [userUpvotes, setUserUpvotes] = useState(new Set()); // Track user's upvotes
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchFeedback();
    loadUserVotes();
  }, []);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('feedback_kb2024')
        .select('id, title, description, category, feedback_type, source, author, created_at, upvotes')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setFeedback(data || []);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setError('Unable to load feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load user's votes from localStorage
  const loadUserVotes = () => {
    try {
      const savedVotes = localStorage.getItem('pal-ng-feedback-votes');
      if (savedVotes) {
        setUserUpvotes(new Set(JSON.parse(savedVotes)));
      }
    } catch (error) {
      console.error('Error loading user votes:', error);
    }
  };

  // Save user's votes to localStorage
  const saveUserVotes = (votes) => {
    try {
      localStorage.setItem('pal-ng-feedback-votes', JSON.stringify([...votes]));
    } catch (error) {
      console.error('Error saving user votes:', error);
    }
  };

  // Filter and sort feedback
  const getFilteredAndSortedFeedback = () => {
    let filtered = feedback.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !categoryFilter || item.category === categoryFilter;
      const matchesFeedbackType = !feedbackTypeFilter || item.feedback_type === feedbackTypeFilter;
      const matchesSource = !sourceFilter || item.source === sourceFilter;

      return matchesSearch && matchesCategory && matchesFeedbackType && matchesSource;
    });

    // Sort by selected criteria
    if (sortBy === 'upvotes') {
      filtered.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
    } else {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return filtered;
  };

  const filteredFeedback = getFilteredAndSortedFeedback();

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      setExportMessage({ type: '', text: '' });

      // Prepare filter information for the export
      const appliedFilters = {
        search: searchTerm,
        category: categoryFilter,
        feedbackType: feedbackTypeFilter,
        source: sourceFilter,
        sortBy: sortBy
      };

      // Export filtered feedback to Excel
      const result = exportFeedbackToExcel(filteredFeedback, appliedFilters);

      if (result.success) {
        setExportMessage({
          type: 'success',
          text: `Successfully exported ${result.recordCount} feedback items to ${result.filename}`
        });
        // Clear message after 5 seconds
        setTimeout(() => {
          setExportMessage({ type: '', text: '' });
        }, 5000);
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportMessage({
        type: 'error',
        text: `Export failed: ${error.message}`
      });
      // Clear message after 5 seconds
      setTimeout(() => {
        setExportMessage({ type: '', text: '' });
      }, 5000);
    } finally {
      setExporting(false);
    }
  };

  const handleUpvote = async (feedbackId) => {
    try {
      const newUpvotes = new Set(userUpvotes);
      const isCurrentlyUpvoted = newUpvotes.has(feedbackId);
      const currentFeedback = feedback.find(item => item.id === feedbackId);
      
      if (!currentFeedback) return;

      let newUpvoteCount;
      
      if (isCurrentlyUpvoted) {
        // Remove upvote
        newUpvotes.delete(feedbackId);
        newUpvoteCount = Math.max(0, (currentFeedback.upvotes || 0) - 1);
      } else {
        // Add upvote
        newUpvotes.add(feedbackId);
        newUpvoteCount = (currentFeedback.upvotes || 0) + 1;
      }

      // Update local state immediately for better UX
      setUserUpvotes(newUpvotes);
      saveUserVotes(newUpvotes);

      // Update local feedback state
      const updatedFeedback = feedback.map(item => {
        if (item.id === feedbackId) {
          return { ...item, upvotes: newUpvoteCount };
        }
        return item;
      });
      setFeedback(updatedFeedback);

      // Update database
      const { error } = await supabase
        .from('feedback_kb2024')
        .update({ upvotes: newUpvoteCount })
        .eq('id', feedbackId);

      if (error) {
        console.error('Error updating upvotes in database:', error);
        // Revert local changes if database update fails
        const revertedUpvotes = new Set(userUpvotes);
        if (isCurrentlyUpvoted) {
          revertedUpvotes.add(feedbackId);
        } else {
          revertedUpvotes.delete(feedbackId);
        }
        setUserUpvotes(revertedUpvotes);
        saveUserVotes(revertedUpvotes);
        
        const revertedFeedback = feedback.map(item => {
          if (item.id === feedbackId) {
            return { ...item, upvotes: currentFeedback.upvotes };
          }
          return item;
        });
        setFeedback(revertedFeedback);
      }
    } catch (error) {
      console.error('Error handling upvote:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 150) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiLoader} className="w-8 h-8 text-orange-600 animate-spin" />
            <span className="text-lg text-gray-600">Loading feedback...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="flex items-center gap-3 mb-2">
          <SafeIcon icon={FiMessageSquare} className="w-8 h-8 text-orange-600" />
          <h1 className="text-3xl font-bold text-gray-900">Feedback</h1>
        </div>
        <p className="text-gray-600">Share your suggestions and help us improve the platform</p>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6"
        >
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchFeedback}
            className="mt-2 text-red-600 hover:text-red-700 font-medium"
          >
            Try Again
          </button>
        </motion.div>
      )}

      {/* Export Message */}
      {exportMessage.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-lg border ${exportMessage.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
            }`}
        >
          <div className="flex items-center gap-2">
            <SafeIcon icon={exportMessage.type === 'success' ? FiCheckCircle : FiAlertTriangle} className="w-4 h-4" />
            <span className="text-sm font-medium">{exportMessage.text}</span>
          </div>
        </motion.div>
      )}

      {/* Action Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6 flex flex-col sm:flex-row gap-4"
      >
        <button
          onClick={() => onNavigate('feedback-form')}
          className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          Submit Feedback
        </button>

        <button
          onClick={handleExportToExcel}
          disabled={exporting || filteredFeedback.length === 0}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? (
            <>
              <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <SafeIcon icon={FiDownload} className="w-5 h-5" />
              Export to Excel ({filteredFeedback.length})
            </>
          )}
        </button>
      </motion.div>

      {/* Search and Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-4">
          <div className="xl:col-span-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search feedback..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={feedbackTypeFilter}
              onChange={(e) => setFeedbackTypeFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              {FEEDBACK_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="">All Sources</option>
              {FEEDBACK_SOURCES.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>

          <div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="recent">Most Recent</option>
              <option value="upvotes">Most Upvoted</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Feedback List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100"
      >
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {filteredFeedback.length} Feedback Item{filteredFeedback.length !== 1 ? 's' : ''}
          </h2>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <SafeIcon icon={sortBy === 'recent' ? FiClock : FiTrendingUp} className="w-4 h-4" />
            <span>Sorted by {sortBy === 'recent' ? 'Most Recent' : 'Most Upvoted'}</span>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {filteredFeedback.map((feedbackItem, index) => (
            <motion.div
              key={feedbackItem.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + index * 0.05 }}
              className="p-6 hover:bg-gray-50 transition-colors duration-200"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 hover:text-orange-600 transition-colors cursor-pointer">
                  {feedbackItem.title}
                </h3>

                <button
                  onClick={() => handleUpvote(feedbackItem.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 ${userUpvotes.has(feedbackItem.id)
                    ? 'bg-orange-100 border-orange-300 text-orange-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <SafeIcon icon={FiThumbsUp} className="w-4 h-4" />
                  <span className="font-medium">
                    {feedbackItem.upvotes || 0}
                  </span>
                </button>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                {truncateText(feedbackItem.description)}
              </p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(feedbackItem.category)}`}>
                  {feedbackItem.category}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getFeedbackTypeColor(feedbackItem.feedback_type)}`}>
                  {feedbackItem.feedback_type}
                </span>
                {feedbackItem.source && (
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSourceColor(feedbackItem.source)}`}>
                    {feedbackItem.source}
                  </span>
                )}
              </div>

              <div className="text-sm text-gray-600">
                Submitted by <span className="font-medium">{feedbackItem.author}</span> • {formatDate(feedbackItem.created_at)}
              </div>
            </motion.div>
          ))}
        </div>

        {filteredFeedback.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center py-12"
          >
            <SafeIcon icon={FiMessageSquare} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No feedback found</h3>
            <p className="text-gray-600">
              Try adjusting your search terms or filters to find feedback
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default FeedbackPage;